import type { PostHog } from "posthog-js";

const posthogToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN as string | undefined;

// Capture in production builds; in dev only when explicitly opted in via
// VITE_POSTHOG_DEV=true. Keeps localhost noise out of the analytics project
// while leaving the env/compose plumbing intact.
const posthogEnabled =
  Boolean(posthogToken) && (import.meta.env.PROD || import.meta.env.VITE_POSTHOG_DEV === "true");

// Marketing surfaces stay fully cookieless (banner-free, nothing stored on the
// device, no identify) so anonymous visitors are consent-exempt. The
// authenticated app boots in identified mode so a signed-in user's sessions
// stitch together — the prerequisite for measuring retention / repeat-comparison
// cadence. See docs/product/ANALYTICS.md.
//
// The mode is chosen once, from the entry URL, because PostHog's persistence
// mode is fixed at init() and authenticated entry into the app is always a full
// page load (OAuth redirect into /auth/callback, or a direct/bookmarked /app
// hit) — so the app boots fresh in app-mode. Marketing→app links that an already
// signed-in user might click are forced to full navigations for the same reason.
function isAppSurface(): boolean {
  const path = window.location.pathname;
  return (
    path === "/app" ||
    path.startsWith("/app/") ||
    path.startsWith("/auth") ||
    path.startsWith("/oauth")
  );
}

// posthog-js is loaded lazily (dynamic import) so its ~60 KiB never blocks the
// landing page's first paint. The module is a singleton, so the instance this
// resolves to is the same one @posthog/react's usePostHog() hands to consumers
// in the lazily-loaded app routes — initializing it here initializes it for
// everyone.
let loader: Promise<PostHog | null> | null = null;

function loadPostHog(): Promise<PostHog | null> {
  if (!posthogEnabled) {
    return Promise.resolve(null);
  }
  if (!loader) {
    const appSurface = isAppSurface();
    loader = import("posthog-js")
      .then(({ default: posthog }) => {
        posthog.init(posthogToken!, {
          api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string,
          // api_host is a first-party reverse proxy in prod; ui_host keeps PostHog's
          // "open in PostHog" links pointing at the real dashboard.
          ui_host: "https://us.posthog.com",
          defaults: "2026-01-30",
          autocapture: false,
          capture_pageview: false,
          capture_pageleave: false,
          capture_dead_clicks: false,
          disable_session_recording: true,
          // Filter out automation/headless browsers (e.g. Checkly monitoring) so
          // synthetic checks don't register as real visits.
          opt_out_useragent_filter: false,
          ...(appSurface
            ? {
                // Authenticated app: persist a distinct id so identify() can
                // stitch a user's sessions. Events stay profile-less until
                // identifyAnalyticsUser() runs. The app encodes project path and
                // branch names in the URL, so deny URL-borne props as a backstop
                // against leaking identifiers via $current_url on every event.
                person_profiles: "identified_only",
                property_denylist: ["$current_url", "$pathname", "$initial_current_url"],
              }
            : {
                // Marketing / anonymous surfaces: store nothing, no banner,
                // no identify. Counts uniques via PostHog's server-side hash.
                cookieless_mode: "always",
                person_profiles: "never",
              }),
        });
        return posthog;
      })
      .catch(() => null);
  }
  return loader;
}

/**
 * Begin loading + initializing PostHog without blocking first paint. Safe to
 * call once on startup; subsequent calls reuse the in-flight load.
 */
export function initAnalytics(): void {
  if (!posthogEnabled) {
    return;
  }
  const start = () => void loadPostHog();
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(start, { timeout: 2000 });
  } else {
    window.setTimeout(start, 1);
  }
}

/**
 * Capture an event, loading PostHog on demand if it hasn't started yet. Used by
 * eager (landing-page) code that must not statically import posthog-js. The
 * event fires once PostHog has initialized, so it is never dropped.
 */
export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  void loadPostHog().then((posthog) => posthog?.capture(event, properties));
}

/**
 * Capture an event that immediately precedes a navigation this code does not
 * perform itself — e.g. the OAuth redirect started by a provider adapter.
 *
 * Like `captureEvent` it waits for initialization, so it is never dropped by the
 * deferred `init()`; like `captureEventThenNavigate` it sends through beacon so
 * the request survives the page unload instead of dying in PostHog's batch
 * queue. Unlike either, it never navigates — the caller's own flow does that.
 */
export function captureEventInstantly(event: string, properties?: Record<string, unknown>): void {
  void loadPostHog().then((posthog) =>
    posthog?.capture(event, properties, {
      send_instantly: true,
      transport: "sendBeacon",
    })
  );
}

/**
 * Capture a marketing event before a full-page navigation.
 *
 * A regular fire-and-forget capture can be abandoned while the lazy PostHog chunk is
 * still loading. Give initialization a small, bounded window, send through beacon once
 * ready, and always continue navigation even when analytics is disabled or unavailable.
 * This intentionally keeps marketing analytics cookieless; it writes no browser state.
 */
export function captureEventThenNavigate(
  event: string,
  properties: Record<string, unknown> | undefined,
  href: string,
  maxWaitMs = 250
): void {
  let navigated = false;
  const navigate = () => {
    if (navigated) return;
    navigated = true;
    window.location.assign(href);
  };
  const timeout = window.setTimeout(navigate, maxWaitMs);

  void loadPostHog()
    .then((posthog) => {
      posthog?.capture(event, properties, {
        send_instantly: true,
        transport: "sendBeacon",
      });
    })
    .finally(() => {
      window.clearTimeout(timeout);
      navigate();
    });
}

/**
 * Tie subsequent app events to a stable pseudonymous user id (the `analyticsId`
 * from sessionContext — a sha256 of the internal user id, never PII). This is
 * what makes a signed-in user's sessions stitch together for retention/cadence.
 * No-op on cookieless marketing surfaces (where identify is unsupported) and
 * when already identified as this user. Pair with posthog.reset() on sign-out.
 */
export function identifyAnalyticsUser(analyticsId: string): void {
  void loadPostHog().then((posthog) => {
    if (!posthog) return;
    if (posthog.get_distinct_id?.() === analyticsId) return;
    posthog.identify(analyticsId);
  });
}
